import React, { useState, useEffect, useRef } from 'react';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ListItemButton } from '@mui/material';
import { dateToShortDayName, formatDate } from '../helpers/Helpers';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Stack } from '@mui/material';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import Badge from '@mui/material/Badge';
import {Box} from '@mui/material';
import { formatDateTime } from '../helpers/Helpers';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { truncateString } from '../helpers/Helpers';
import OfficePostsClient from './OfficePostsClient';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(1),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
}));  

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: '#44b700',
        color: '#44b700',
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor',
            content: '""',
        },
    },
    '@keyframes ripple': {
        '0%': {
            transform: 'scale(.8)',
            opacity: 1,
        },
        '100%': {
            transform: 'scale(2.4)',
            opacity: 0,
        },
    },
}));
  

function AppointmentsListItem(props) {

    const [date, setDate] = useState(dateToShortDayName(props.appointment.date) + ', ' + formatDate(props.appointment.date));

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = (event, reason) => {
        if (reason === 'clickaway' || reason === 'backdropClick') {
            return;
        }
        setOpen(false);
    };
 
    return (
        <React.Fragment>
            <ListItemButton onClick={handleClickOpen} alignItems="flex-start">
                <ListItemAvatar>
                    { props.appointment.now_serving == props.appointment.customer_number ?
                    <StyledBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot">
                        <Avatar style={{ background: '#86BBD8' }}>
                            {date == "TODAY" ? <ScheduleIcon /> : <CalendarMonthIcon /> }
                        </Avatar>  
                    </StyledBadge>
                    :
                    <Avatar style={{ background: '#86BBD8' }}>
                        {date == "TODAY" ? <ScheduleIcon /> : <CalendarMonthIcon /> }
                    </Avatar>  
                    }
                </ListItemAvatar>
                <ListItemText style={{whiteSpace: "pre-line", verticalAlign: "bottom"}}
                    primary={props.appointment.office_name}
                    secondary={
                        <React.Fragment>
                            <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                                alignItems='center'
                            >
                            {date}
                            </Typography>
                            { date == "TODAY" ? <React.Fragment><br /><b>Now serving {props.appointment.now_serving}. You are {props.appointment.customer_number}.</b></React.Fragment> : null } { props.appointment.paused ? <React.Fragment> <b>(paused)</b> </React.Fragment> : null }
                            { date == "TODAY" ? <React.Fragment><br /><b>On-site time: {props.estTime}</b></React.Fragment> : null }
                            { date == "TODAY" && props.appointment.office_notes ? <React.Fragment><br />{props.appointment.office_notes}</React.Fragment> : null }
                        </React.Fragment>
                    }
                >
                </ListItemText>
            </ListItemButton>
            <Divider variant="middle" component="li" />


            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="appointment-dialog-title"
                open={open}
                sx={{
                    "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "420px",
                        borderRadius: 1, 
                    },
                    },
                }}
                >
                <DialogTitle
                    id="appointment-dialog-title"
                    sx={{
                    // bgcolor: "#86bbd8",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    }}
                >
                    <Typography variant="h6" component="span">
                    {props.appointment.office_name}
                    </Typography>

                    <Typography variant="body2">
                        {props.appointment.start_time} – {props.appointment.end_time}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    <Stack alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" color="text.primary">
                        {date}
                    </Typography>

                    {props.appointment.paused && (
                        <Chip label="Paused" size="small" color="info" sx={{ mb: 1 }} />
                    )}

                    {/* ✅ Horizontal layout for Now Serving & You Are */}
                    <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center" 
                    justifyContent="center"
                    divider={<Divider orientation="vertical" flexItem />}
                    >
                    <Stack alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                        Now Serving
                        </Typography>
                        <Typography variant="h4">{props.appointment.now_serving}</Typography>
                    </Stack>

                    <Stack alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                        You Are
                        </Typography>
                        <Typography variant="h4">{props.appointment.customer_number}</Typography>
                    </Stack>
                    </Stack>

                        <Stack spacing={0} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                            On-site time
                            </Typography>
                            <Typography variant="h6" component="span">
                            {props.estTime}
                            </Typography>
                        </Stack>
                    </Stack>

                    {props.appointment.office_notes ?
                        <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={1} // space between icon and text
                        padding={1}
                        >
                        {/* Left column: Icon */}
                        <StickyNote2Icon sx={{ color: 'gray' }} />

                        {/* Right column: Text stacked vertically */}
                            <Stack direction="column" spacing={0}>
                                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    Status
                                </Typography>
                                <Typography
                                style={{ whiteSpace: "pre-line", verticalAlign: "bottom" }}
                                sx={{ lineHeight: 1.2 }}
                                component="div"
                                >
                                    {truncateString(props.appointment.office_notes, 100)}
                                </Typography>
                            </Stack>
                        </Stack>
                    : null }

                    {props.appointment.office_latest_post ?
                        <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={1} // space between icon and text
                        padding={1}
                        >
                        {/* Left column: Icon */}
                        <CampaignIcon sx={{ color: 'gray' }} />

                        {/* Right column: Text stacked vertically */}
                            <Stack direction="column" spacing={0}>
                                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    Latest Advisory<br />
                                    {formatDateTime(props.appointment.office_latest_post_date)}
                                </Typography>
                                <Typography
                                    style={{ whiteSpace: "pre-line", verticalAlign: "bottom" }}
                                    sx={{ lineHeight: 1.2 }}
                                    component="div"
                                >
                                    {truncateString(props.appointment.office_latest_post, 100)}
                                </Typography>
                                <OfficePostsClient officeId={props.appointment.office_id} officeName={props.appointment.office_name} />
                            </Stack>
                        </Stack>
                    : null }
                </DialogContent>

                <DialogActions sx={{ p: 1.5 }}>
                {props.cancelling && <CircularProgress size="1.5rem" />}
                
                {/* Left-aligned Cancel Appointment */}
                <Button
                    size="small"
                    disabled={
                    props.appointment.now_serving === props.appointment.customer_number ||
                    props.cancelling
                    }
                    onClick={props.onCancelButtonClick}
                    data-id={props.appointment.id}
                >
                    Cancel Appointment
                </Button>

                {/* Spacer to push Close to far right */}
                <Box sx={{ flex: 1 }} />

                {/* Right-aligned Close */}
                <Button size="small" disabled={props.cancelling} onClick={handleClose}>
                    Close
                </Button>
                </DialogActions>

                </BootstrapDialog>
        </React.Fragment>
      );
}

export default AppointmentsListItem;